import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiCalendar, FiCheck, FiLock, FiUnlock, FiAlertCircle, FiX, FiSave, FiExternalLink, FiEdit2 } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { collection, updateDoc, doc, getDocs, query, orderBy, where, limit, writeBatch } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { TRAN_TYPE_LABELS, TRAN_TYPE_ROUTES } from '../../utils/navigationHelper';
import PageLayout from '../../components/PageLayout';
import usePagePermissions from '../../hooks/usePagePermissions';
import useEscapeClose from '../../hooks/useEscapeClose';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeaderText = styled.div``;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 8px 0;
`;

const PageDescription = styled.p`
  font-size: 14px;
  color: ${colors.text.secondary};
  margin: 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${colors.primary.dark};
    transform: translateY(-1px);
  }
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 14px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[200]};
  transition: background 0.2s;

  &:hover {
    background: ${colors.neutral[50]};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: ${colors.text.primary};
  vertical-align: middle;
`;

const YearCode = styled.span`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-weight: 600;
  color: ${colors.primary.main};
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    if (props.$active) return colors.success.main + '20';
    if (props.$closed) return colors.neutral[200];
    return colors.warning.main + '20';
  }};
  color: ${props => {
    if (props.$active) return colors.success.main;
    if (props.$closed) return colors.text.secondary;
    return colors.warning.main;
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;

  ${props => props.$primary && `
    background: ${colors.primary.main};
    border-color: ${colors.primary.main};
    color: white;

    &:hover {
      background: ${colors.primary.dark};
      border-color: ${colors.primary.dark};
    }
  `}

  ${props => props.$warning && `
    background: ${colors.warning.main}20;
    border-color: ${colors.warning.main};
    color: ${colors.warning.main};

    &:hover {
      background: ${colors.warning.main}30;
    }
  `}

  ${props => props.$danger && `
    background: ${colors.error.main}20;
    border-color: ${colors.error.main};
    color: ${colors.error.main};

    &:hover {
      background: ${colors.error.main}30;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DateRange = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DateText = styled.span`
  font-size: 13px;
  color: ${colors.text.primary};
`;

const DateLabel = styled.span`
  font-size: 11px;
  color: ${colors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  color: ${colors.neutral[300]};
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin: 0 0 8px 0;
`;

const EmptySubtext = styled.p`
  font-size: 14px;
  margin: 0;
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  margin-bottom: 20px;
  background: ${props => props.$type === 'success' ? colors.success.main + '15' :
    props.$type === 'warning' ? colors.warning.main + '15' : colors.error.main + '15'};
  color: ${props => props.$type === 'success' ? colors.success.main :
    props.$type === 'warning' ? colors.warning.main : colors.error.main};
  border: 1px solid ${props => props.$type === 'success' ? colors.success.main + '30' :
    props.$type === 'warning' ? colors.warning.main + '30' : colors.error.main + '30'};
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${colors.shadows.lg};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: ${colors.text.secondary};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${colors.borderRadius.sm};
  transition: all 0.2s;

  &:hover {
    background: ${colors.neutral[100]};
    color: ${colors.text.primary};
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text.primary};
  margin-bottom: 6px;

  span {
    color: ${colors.error.main};
    margin-left: 2px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &:disabled {
    background: ${colors.neutral[100]};
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: white;
  color: ${colors.text.primary};
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${colors.primary.dark};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const InfoBox = styled.div`
  background: ${colors.primary.main}10;
  border: 1px solid ${colors.primary.main}30;
  border-radius: ${colors.borderRadius.md};
  padding: 12px 16px;
  margin-top: 16px;
  font-size: 13px;
  color: ${colors.text.secondary};
  line-height: 1.5;
`;

const DraftListCard = styled.div`
  background: ${colors.error.main}08;
  border: 1px solid ${colors.error.main}30;
  border-radius: ${colors.borderRadius.lg};
  margin-bottom: 20px;
  overflow: hidden;
`;

const DraftListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: ${colors.error.main}12;
  border-bottom: 1px solid ${colors.error.main}20;
`;

const DraftListTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.error.main};
`;

const DraftDismissBtn = styled.button`
  background: none;
  border: none;
  color: ${colors.error.main};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  &:hover { background: ${colors.error.main}15; }
`;

const DraftTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const DraftTh = styled.th`
  text-align: left;
  padding: 8px 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.error.main}15;
`;

const DraftTr = styled.tr`
  &:hover { background: ${colors.error.main}06; }
  &:not(:last-child) td { border-bottom: 1px solid ${colors.border.light}; }
`;

const DraftTd = styled.td`
  padding: 8px 12px;
  color: ${colors.text.primary};
`;

const DraftTypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${colors.error.main}15;
  color: ${colors.error.main};
`;

const DraftGoBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: 1px solid ${colors.primary.main};
  border-radius: 4px;
  background: white;
  color: ${colors.primary.main};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  &:hover { background: ${colors.primary.main}10; }
`;

const FinancialYear = () => {
  const { activeCompany, currentUser, refreshActiveFinancialYear } = useAuth();
  const { canEdit } = usePagePermissions('settings', 'financialYear');
  const navigate = useNavigate();
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  useEscapeClose(showModal, () => closeModal());
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [draftTransactions, setDraftTransactions] = useState([]);
  // Edit mode: the year being edited (null = create mode). When the year already
  // has transactions, editLocked is true and its dates are read-only (the user's
  // chosen rule) so no posted entry can ever fall outside its own period.
  const [editingYear, setEditingYear] = useState(null);
  const [editLocked, setEditLocked] = useState(false);
  const [formData, setFormData] = useState({
    yearCode: '',
    yearName: '',
    startDate: '',
    endDate: '',
    fiscalYearStart: 'July',
    numYears: 1
  });

  const closeModal = () => {
    setShowModal(false);
    setEditingYear(null);
    setEditLocked(false);
  };

  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;

    try {
      setLoading(true);
      const yearsRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(yearsRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);

      const yearsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setYears(yearsList);
    } catch (error) {
      console.error('Error loading financial years:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to load financial years' });
    } finally {
      setLoading(false);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadFinancialYears();
  }, [loadFinancialYears]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-PK', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const generateYearCode = (startDate) => {
    const start = new Date(startDate);
    const startYear = start.getFullYear();
    const endYear = startYear + 1;
    return `${startYear}-${String(endYear).slice(-2)}`;
  };

  const generateYearName = (startDate) => {
    const code = generateYearCode(startDate);
    return `Financial Year ${code}`;
  };

  const calculateEndDate = (startDate) => {
    const start = new Date(startDate);
    const endDate = new Date(start.getFullYear() + 1, start.getMonth(), start.getDate() - 1);
    return endDate.toISOString().split('T')[0];
  };

  const handleStartDateChange = (e) => {
    const startDate = e.target.value;
    const yearCode = generateYearCode(startDate);
    const yearName = generateYearName(startDate);
    const endDate = calculateEndDate(startDate);

    setFormData(prev => ({
      ...prev,
      startDate,
      endDate,
      yearCode,
      yearName
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const openNewYearModal = () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    setEditingYear(null);
    setEditLocked(false);
    // Pre-fill based on company's fiscal year start
    const fiscalStart = activeCompany?.fiscalYearStart || 'July';
    const monthMap = {
      'January': 0, 'February': 1, 'March': 2, 'April': 3,
      'May': 4, 'June': 5, 'July': 6, 'August': 7,
      'September': 8, 'October': 9, 'November': 10, 'December': 11
    };

    const today = new Date();
    let suggestedYear = today.getFullYear();
    const fiscalMonth = monthMap[fiscalStart];

    // If we're past the fiscal year start month, suggest current year
    // Otherwise suggest previous year
    if (today.getMonth() < fiscalMonth) {
      suggestedYear--;
    }

    const startDate = new Date(suggestedYear, fiscalMonth, 1);
    const startDateStr = startDate.toISOString().split('T')[0];

    setFormData({
      yearCode: generateYearCode(startDateStr),
      yearName: generateYearName(startDateStr),
      startDate: startDateStr,
      endDate: calculateEndDate(startDateStr),
      fiscalYearStart: fiscalStart,
      numYears: 1
    });

    setShowModal(true);
  };

  // Open the modal to edit an existing (non-closed) year. We check whether the
  // year already has any transactions; if so, its dates are locked (editLocked)
  // so editing can never push posted entries outside their period.
  const openEditYearModal = async (year) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (year.isClosed) {
      setAlert({ show: true, type: 'error', message: 'Closed years cannot be edited.' });
      return;
    }
    setEditingYear(year);
    setEditLocked(false);
    setFormData({
      yearCode: year.yearCode || '',
      yearName: year.yearName || '',
      startDate: year.startDate || '',
      endDate: year.endDate || '',
      fiscalYearStart: activeCompany?.fiscalYearStart || 'July',
      numYears: 1
    });
    setShowModal(true);
    // Lock dates if the year already holds transactions
    try {
      const txQ = query(
        collection(db, 'companies', activeCompany.id, 'transactions'),
        where('financialYearId', '==', year.id),
        limit(1)
      );
      const snap = await getDocs(txQ);
      setEditLocked(!snap.empty);
    } catch (e) {
      console.warn('Could not check transactions for year; locking dates as a precaution:', e);
      setEditLocked(true);
    }
  };

  // Overlap test against existing years, optionally excluding one (for edits).
  const overlapsExisting = (startDate, endDate, excludeId = null) => {
    const s = new Date(startDate);
    const e = new Date(endDate);
    return years.find(fy =>
      fy.id !== excludeId &&
      s <= new Date(fy.endDate) &&
      e >= new Date(fy.startDate)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }

    if (!formData.startDate || !formData.endDate) {
      setAlert({ show: true, type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    setSaving(true);
    try {
      const yearsRef = collection(db, 'companies', activeCompany.id, 'financialYears');

      // ============================================================
      // EDIT EXISTING YEAR
      // ============================================================
      if (editingYear) {
        // When the year already has transactions, its dates are locked: keep the
        // originals regardless of what the (disabled) inputs show.
        const startDate = editLocked ? editingYear.startDate : formData.startDate;
        const endDate = editLocked ? editingYear.endDate : formData.endDate;

        if (years.some(y => y.id !== editingYear.id && y.yearCode === formData.yearCode)) {
          setAlert({ show: true, type: 'error', message: 'A financial year with this code already exists' });
          return;
        }
        const overlap = overlapsExisting(startDate, endDate, editingYear.id);
        if (overlap) {
          setAlert({ show: true, type: 'error', message: `Date range overlaps with existing financial year "${overlap.yearName}"` });
          return;
        }

        await updateDoc(doc(db, 'companies', activeCompany.id, 'financialYears', editingYear.id), {
          yearCode: formData.yearCode,
          yearName: formData.yearName,
          startDate,
          endDate,
          updatedAt: new Date().toISOString(),
          updatedBy: currentUser?.uid
        });

        // If the active year's range changed, refresh context so validation uses it.
        if (editingYear.isActive && !editLocked) {
          await refreshActiveFinancialYear();
        }

        setAlert({ show: true, type: 'success', message: 'Financial year updated successfully' });
        closeModal();
        loadFinancialYears();
        return;
      }

      // ============================================================
      // CREATE ONE OR MANY CONSECUTIVE YEARS
      // ============================================================
      const count = Math.min(50, Math.max(1, parseInt(formData.numYears, 10) || 1));
      const isFirstEver = years.length === 0;
      // Recovery: if the company currently has NO active financial year (e.g. all
      // were closed previously), the newly created year should become active so
      // the company is never left without a postable year.
      const noActiveYear = years.length > 0 && !years.some(y => y.isActive);

      // Build the set. For a single year, respect any manual edits to end
      // date / code / name (original behaviour). For multiple, auto-generate
      // consecutive one-year periods, each starting the day after the prior ends.
      const planned = [];
      if (count === 1) {
        planned.push({
          startDate: formData.startDate,
          endDate: formData.endDate,
          yearCode: formData.yearCode || generateYearCode(formData.startDate),
          yearName: formData.yearName || generateYearName(formData.startDate)
        });
      } else {
        let cursorStart = formData.startDate;
        for (let i = 0; i < count; i++) {
          const endDate = calculateEndDate(cursorStart);
          planned.push({
            startDate: cursorStart,
            endDate,
            yearCode: generateYearCode(cursorStart),
            yearName: generateYearName(cursorStart)
          });
          const nextStart = new Date(endDate);
          nextStart.setDate(nextStart.getDate() + 1);
          cursorStart = nextStart.toISOString().split('T')[0];
        }
      }

      // Validate each against existing years AND against the rest of the planned set.
      for (const p of planned) {
        if (years.some(y => y.yearCode === p.yearCode) ||
            planned.filter(x => x.yearCode === p.yearCode).length > 1) {
          setAlert({ show: true, type: 'error', message: `Year code "${p.yearCode}" already exists or repeats in this batch` });
          return;
        }
        const clash = overlapsExisting(p.startDate, p.endDate);
        if (clash) {
          setAlert({ show: true, type: 'error', message: `"${p.yearName}" overlaps with existing financial year "${clash.yearName}"` });
          return;
        }
      }

      // previousYearId for the first new year links into the existing chain.
      let prevId = null;
      if (years.length > 0) {
        const sorted = [...years].sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
        prevId = sorted.find(y => y.isClosed)?.id || sorted[0]?.id || null;
      }

      // Pre-generate refs so we can chain previousYearId across the batch.
      const batch = writeBatch(db);
      const refs = planned.map(() => doc(yearsRef));
      planned.forEach((p, i) => {
        batch.set(refs[i], {
          yearCode: p.yearCode,
          yearName: p.yearName,
          startDate: p.startDate,
          endDate: p.endDate,
          isActive: (isFirstEver || noActiveYear) && i === 0, // first-ever, or recover when none is active
          isClosed: false,
          closedAt: null,
          closedBy: null,
          previousYearId: i === 0 ? prevId : refs[i - 1].id,
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.uid
        });
      });
      await batch.commit();

      if (isFirstEver || noActiveYear) {
        await refreshActiveFinancialYear();
      }

      setAlert({
        show: true,
        type: 'success',
        message: count > 1 ? `${count} financial years created successfully` : 'Financial year created successfully'
      });
      closeModal();
      loadFinancialYears();
    } catch (error) {
      console.error('Error saving financial year(s):', error);
      setAlert({ show: true, type: 'error', message: 'Failed to save financial year(s)' });
    } finally {
      setSaving(false);
    }
  };

  const handleSetActive = async (yearId) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    const year = years.find(y => y.id === yearId);
    if (year.isClosed) {
      setAlert({ show: true, type: 'error', message: 'Cannot activate a closed financial year' });
      return;
    }

    // Warn if the chosen year doesn't include today. While it's active, new
    // entries dated today are rejected as "outside financial year", which
    // confuses users. Allow it (they may be reviewing or posting back-dated
    // entries), but make it a conscious choice rather than a silent surprise.
    const today = new Date().toISOString().split('T')[0];
    if (year?.startDate && year?.endDate && (today < year.startDate || today > year.endDate)) {
      if (!window.confirm(
        `Heads up: today (${today}) is OUTSIDE "${year.yearName || year.yearCode}" (${year.startDate} to ${year.endDate}).\n\n` +
        `While this year is active, new entries dated today will be rejected as "outside financial year".\n\n` +
        `Activate it anyway?`
      )) {
        return;
      }
    }

    try {
      // Deactivate all years first
      const batch = [];
      for (const y of years) {
        if (y.isActive) {
          batch.push(updateDoc(doc(db, 'companies', activeCompany.id, 'financialYears', y.id), {
            isActive: false
          }));
        }
      }
      await Promise.all(batch);

      // Activate selected year
      await updateDoc(doc(db, 'companies', activeCompany.id, 'financialYears', yearId), {
        isActive: true
      });

      // Refresh the active financial year in the auth context
      await refreshActiveFinancialYear();

      setAlert({ show: true, type: 'success', message: 'Active financial year changed successfully' });
      loadFinancialYears();
    } catch (error) {
      console.error('Error setting active year:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to change active year' });
    }
  };

  const handleCloseYear = async (yearId) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    const year = years.find(y => y.id === yearId);
    if (!year) return;

    // Guard: at least one financial year must always stay active/open. You can
    // close any past (inactive) year, but not the ACTIVE one, and not the last
    // open one — otherwise the company is left with no year to post into.
    const otherOpenYears = years.filter(y => y.id !== yearId && !y.isClosed);
    if (year.isActive) {
      setAlert({ show: true, type: 'error', message: otherOpenYears.length > 0
        ? 'This is the active financial year. Activate another financial year first — at least one year must always stay active.'
        : 'This is the active financial year and the only open one. At least one financial year must stay active, so it cannot be closed.' });
      return;
    }
    if (otherOpenYears.length === 0) {
      setAlert({ show: true, type: 'error', message: 'At least one financial year must remain open. You cannot close the last one.' });
      return;
    }

    if (!window.confirm(`Are you sure you want to close "${year.yearName}"?\n\nOnce closed, no new transactions can be posted to this year. This action cannot be undone.`)) {
      return;
    }

    try {
      // Check for open draft transactions in this financial year
      const draftQuery = query(
        collection(db, 'companies', activeCompany.id, 'transactions'),
        where('financialYearId', '==', yearId),
        where('status', '==', 'Draft')
      );
      const draftSnapshot = await getDocs(draftQuery);
      if (!draftSnapshot.empty) {
        const drafts = draftSnapshot.docs.map(d => ({
          id: d.id,
          docNumber: d.data().docNumber || '-',
          tranTypeCode: d.data().tranTypeCode || '-',
          docDate: d.data().docDate || '-',
          partyName: d.data().partyName || '',
          totalAmount: d.data().totalAmount || d.data().netAmount || 0,
        }));
        setDraftTransactions(drafts);
        setAlert({ show: true, type: 'error', message: `Cannot close: ${drafts.length} Draft transaction(s) found. Post or delete them first.` });
        return;
      }

      setDraftTransactions([]);
      await updateDoc(doc(db, 'companies', activeCompany.id, 'financialYears', yearId), {
        isClosed: true,
        isActive: false,
        closedAt: new Date().toISOString(),
        closedBy: currentUser?.uid
      });

      setAlert({ show: true, type: 'success', message: 'Financial year closed successfully' });
      loadFinancialYears();
    } catch (error) {
      console.error('Error closing year:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to close financial year' });
    }
  };

  const getActiveYear = () => years.find(y => y.isActive);

  return (
    <PageLayout pageTitle="Financial Year">
      <Container>
        <PageHeader>
          <HeaderText>
            <PageTitle>Financial Years</PageTitle>
            <PageDescription>
              Manage your accounting periods. Only one financial year can be active at a time.
            </PageDescription>
          </HeaderText>
          {canEdit && (
            <AddButton onClick={openNewYearModal}>
              <FiPlus />
              New Financial Year
            </AddButton>
          )}
        </PageHeader>

        {alert.show && (
          <Alert $type={alert.type}>
            <FiAlertCircle />
            {alert.message}
          </Alert>
        )}

        {draftTransactions.length > 0 && (
          <DraftListCard>
            <DraftListHeader>
              <DraftListTitle>Draft Transactions Blocking Year Close</DraftListTitle>
              <DraftDismissBtn onClick={() => setDraftTransactions([])}>
                <FiX size={14} />
              </DraftDismissBtn>
            </DraftListHeader>
            <DraftTable>
              <thead>
                <tr>
                  <DraftTh>Type</DraftTh>
                  <DraftTh>Doc Number</DraftTh>
                  <DraftTh>Date</DraftTh>
                  <DraftTh>Party</DraftTh>
                  <DraftTh>Amount</DraftTh>
                  <DraftTh>Action</DraftTh>
                </tr>
              </thead>
              <tbody>
                {draftTransactions.map((txn) => (
                  <DraftTr key={txn.id}>
                    <DraftTd>
                      <DraftTypeBadge>{TRAN_TYPE_LABELS[txn.tranTypeCode] || txn.tranTypeCode}</DraftTypeBadge>
                    </DraftTd>
                    <DraftTd style={{ fontWeight: 500 }}>{txn.docNumber}</DraftTd>
                    <DraftTd>{txn.docDate ? new Date(txn.docDate).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</DraftTd>
                    <DraftTd>{txn.partyName || '-'}</DraftTd>
                    <DraftTd style={{ fontFamily: 'monospace', textAlign: 'right' }}>
                      {txn.totalAmount ? Number(txn.totalAmount).toLocaleString('en-PK', { minimumFractionDigits: 2 }) : '-'}
                    </DraftTd>
                    <DraftTd>
                      {TRAN_TYPE_ROUTES[txn.tranTypeCode] && (
                        <DraftGoBtn onClick={() => navigate(TRAN_TYPE_ROUTES[txn.tranTypeCode], { state: { viewTransactionId: txn.id } })}>
                          Open <FiExternalLink size={12} />
                        </DraftGoBtn>
                      )}
                    </DraftTd>
                  </DraftTr>
                ))}
              </tbody>
            </DraftTable>
          </DraftListCard>
        )}

        {getActiveYear() && (
          <Alert $type="success">
            <FiCheck />
            Current Active Year: <strong>{getActiveYear()?.yearName}</strong> ({formatDate(getActiveYear()?.startDate)} - {formatDate(getActiveYear()?.endDate)})
          </Alert>
        )}

        <ContentCard>
          {loading ? (
            <EmptyState>
              <EmptyText>Loading financial years...</EmptyText>
            </EmptyState>
          ) : years.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiCalendar /></EmptyIcon>
              <EmptyText>No Financial Years Created</EmptyText>
              <EmptySubtext>Create your first financial year to start recording transactions.</EmptySubtext>
            </EmptyState>
          ) : (
            <TableContainer>
              <Table>
                <Thead>
                  <tr>
                    <Th>Year Code</Th>
                    <Th>Year Name</Th>
                    <Th>Period</Th>
                    <Th>Status</Th>
                    <Th>Actions</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {years.map(year => (
                    <Tr key={year.id}>
                      <Td>
                        <YearCode>{year.yearCode}</YearCode>
                      </Td>
                      <Td>{year.yearName}</Td>
                      <Td>
                        <DateRange>
                          <DateText>
                            {formatDate(year.startDate)} - {formatDate(year.endDate)}
                          </DateText>
                        </DateRange>
                      </Td>
                      <Td>
                        {year.isActive ? (
                          <Badge $active>
                            <FiCheck size={12} />
                            Active
                          </Badge>
                        ) : year.isClosed ? (
                          <Badge $closed>
                            <FiLock size={12} />
                            Closed
                          </Badge>
                        ) : (
                          <Badge>
                            <FiUnlock size={12} />
                            Open
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        <ActionButtons>
                          {canEdit && !year.isClosed && (
                            <ActionButton
                              onClick={() => openEditYearModal(year)}
                              title="Edit this financial year"
                              style={{ background: 'white', borderColor: colors.neutral[300], color: colors.text.primary }}
                            >
                              <FiEdit2 size={14} />
                              Edit
                            </ActionButton>
                          )}
                          {canEdit && !year.isActive && !year.isClosed && (
                            <ActionButton
                              $primary
                              onClick={() => handleSetActive(year.id)}
                              title="Set as active year"
                            >
                              <FiCheck size={14} />
                              Set Active
                            </ActionButton>
                          )}
                          {/* Close is hidden for the active year and the last open
                              year — at least one financial year must stay active. */}
                          {canEdit && !year.isClosed && !year.isActive && years.some(y => !y.isClosed && y.id !== year.id) && (
                            <ActionButton
                              $danger
                              onClick={() => handleCloseYear(year.id)}
                              title="Close this financial year"
                            >
                              <FiLock size={14} />
                              Close Year
                            </ActionButton>
                          )}
                          {year.isClosed && (
                            <span style={{ fontSize: '12px', color: colors.text.secondary }}>
                              Closed on {formatDate(year.closedAt)}
                            </span>
                          )}
                        </ActionButtons>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </ContentCard>

        {/* New Financial Year Modal */}
        {showModal && (
          <ModalOverlay>
            <ModalContent onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{editingYear ? 'Edit Financial Year' : 'Create Financial Year'}</ModalTitle>
                <CloseButton onClick={closeModal}>
                  <FiX />
                </CloseButton>
              </ModalHeader>
              <form onSubmit={handleSubmit}>
                <ModalBody>
                  {editingYear && editLocked && (
                    <InfoBox style={{ marginTop: 0, marginBottom: 16, background: colors.warning.main + '12', border: `1px solid ${colors.warning.main}40`, color: colors.warning.main }}>
                      <strong>Dates locked.</strong> This year already has transactions, so its start/end dates can't be changed (this protects your accounting). You can still update the code and name.
                    </InfoBox>
                  )}

                  <FormGroup>
                    <Label>Start Date <span>*</span></Label>
                    <Input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleStartDateChange}
                      disabled={!!editingYear && editLocked}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>End Date <span>*</span></Label>
                    <Input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleChange}
                      disabled={!!editingYear && editLocked}
                      required
                    />
                  </FormGroup>

                  {!editingYear && (
                    <FormGroup>
                      <Label>Number of Years to Create</Label>
                      <Input
                        type="number"
                        name="numYears"
                        min="1"
                        max="50"
                        value={formData.numYears}
                        onChange={handleChange}
                      />
                      {Number(formData.numYears) > 1 && (
                        <InfoBox style={{ marginTop: 8 }}>
                          Creates <strong>{Math.min(50, Math.max(1, parseInt(formData.numYears, 10) || 1))}</strong> consecutive one-year periods starting from the Start Date above. Each links to the previous; none are auto-activated (you pick the active year afterwards). No accounting entries are created.
                        </InfoBox>
                      )}
                    </FormGroup>
                  )}

                  <FormRow>
                    <FormGroup>
                      <Label>Year Code</Label>
                      <Input
                        type="text"
                        name="yearCode"
                        value={formData.yearCode}
                        onChange={handleChange}
                        placeholder="e.g., 2024-25"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Year Name</Label>
                      <Input
                        type="text"
                        name="yearName"
                        value={formData.yearName}
                        onChange={handleChange}
                        placeholder="e.g., Financial Year 2024-25"
                      />
                    </FormGroup>
                  </FormRow>

                  <InfoBox>
                    <strong>Note:</strong> The financial year dates are automatically calculated based on the start date.
                    You can adjust them if needed. All transactions must fall within the active financial year's date range.
                  </InfoBox>
                </ModalBody>
                <ModalFooter>
                  <CancelButton type="button" onClick={closeModal}>
                    Cancel
                  </CancelButton>
                  {canEdit && (
                    <SaveButton type="submit" disabled={saving}>
                      <FiSave />
                      {saving
                        ? 'Saving...'
                        : editingYear
                          ? 'Save Changes'
                          : Number(formData.numYears) > 1
                            ? `Create ${Math.min(50, Math.max(1, parseInt(formData.numYears, 10) || 1))} Years`
                            : 'Create Year'}
                    </SaveButton>
                  )}
                </ModalFooter>
              </form>
            </ModalContent>
          </ModalOverlay>
        )}
      </Container>
    </PageLayout>
  );
};

export default FinancialYear;
